import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { IMG, GALLERY_CATEGORIES } from "@/lib/site";
import { api, imgSrc } from "@/lib/api";

const CATEGORIES = ["All", ...GALLERY_CATEGORIES];

const SPANS = [
  "md:col-span-7", "md:col-span-5", "md:col-span-5", "md:col-span-7",
  "md:col-span-4", "md:col-span-4", "md:col-span-4", "md:col-span-6",
  "md:col-span-6", "md:col-span-5", "md:col-span-7", "md:col-span-12",
];

const PHOTOS = [
  { src: IMG.porsche911, cat: "Vehicle Inventory", span: "md:col-span-7", label: "Porsche 911 Carrera S" },
  { src: IMG.engineBay, cat: "Performance Builds", span: "md:col-span-5", label: "Engine Bay Detail" },
  { src: IMG.bmwBlue, cat: "Vehicle Inventory", span: "md:col-span-5", label: "BMW M4 Competition" },
  { src: IMG.wrenching, cat: "Repairs", span: "md:col-span-7", label: "Precision Repair Work" },
  { src: IMG.repairBay, cat: "Shop Life", span: "md:col-span-4", label: "The TRX Bay" },
  { src: IMG.mEngine, cat: "Performance Builds", span: "md:col-span-4", label: "M Power Plant" },
  { src: IMG.amgGt, cat: "Customer Deliveries", span: "md:col-span-4", label: "AMG GT Delivery Day" },
  { src: IMG.corvette, cat: "Vehicle Inventory", span: "md:col-span-6", label: "Camaro SS 1LE" },
  { src: IMG.perfParts, cat: "Performance Builds", span: "md:col-span-6", label: "Turbo Hardware" },
  { src: IMG.shop, cat: "Shop Life", span: "md:col-span-5", label: "Facility Exterior" },
  { src: IMG.carNight, cat: "Customer Deliveries", span: "md:col-span-7", label: "Night Delivery" },
  { src: IMG.audiNight, cat: "Vehicle Inventory", span: "md:col-span-12", label: "Audi R8 V10" },
];

export default function Gallery() {
  const [cat, setCat] = useState("All");
  const [lightbox, setLightbox] = useState(null);
  const [items, setItems] = useState([]);

  useEffect(() => {
    document.title = "Gallery | TRX Auto Sales & Service Inc";
    api.get("/gallery").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  const photos = useMemo(() => {
    const source = items.length
      ? items.map((g, i) => ({ src: imgSrc(g.image), cat: g.category, label: g.label || g.category, span: SPANS[i % SPANS.length] }))
      : PHOTOS;
    return cat === "All" ? source : source.filter((p) => p.cat === cat);
  }, [cat, items]);

  return (
    <>
      <PageHero
        eyebrow="Gallery"
        lines={["The Work", "Speaks."]}
        sub="Inventory, builds, repairs, shop life, and the moments that matter — delivery day."
        image={IMG.carNight}
      />

      <section className="py-16 md:py-24" data-testid="gallery-section">
        <div className="mx-auto max-w-[1600px] px-6 md:px-10">
          <Reveal>
            <div className="mb-12 flex flex-wrap gap-2" data-testid="gallery-filters">
              {CATEGORIES.map((c) => (
                <button
                  key={c}
                  onClick={() => setCat(c)}
                  data-testid={`gallery-filter-${c.toLowerCase().replace(/\s/g, "-")}`}
                  className={`border px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] transition-all duration-300 ${
                    cat === c
                      ? "border-trx-blue bg-trx-blue text-white"
                      : "border-white/15 text-zinc-400 hover:border-white/40 hover:text-white"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </Reveal>

          <motion.div layout className="grid grid-cols-1 gap-5 md:grid-cols-12" data-testid="gallery-grid">
            <AnimatePresence mode="popLayout">
              {photos.map((p, i) => (
                <motion.button
                  layout
                  key={p.src}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.97 }}
                  transition={{ duration: 0.55, delay: i * 0.04, ease: [0.16, 1, 0.3, 1] }}
                  onClick={() => setLightbox(p)}
                  data-testid={`gallery-photo-${i}`}
                  className={`group relative overflow-hidden border border-white/10 text-left ${p.span}`}
                >
                  <img
                    src={p.src}
                    alt={p.label}
                    loading="lazy"
                    className="aspect-[16/10] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                  <div className="absolute bottom-0 left-0 p-5 opacity-0 transition-all duration-500 group-hover:opacity-100">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-trx-blue">{p.cat}</p>
                    <p className="mt-1 font-display text-lg font-bold text-white">{p.label}</p>
                  </div>
                </motion.button>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] flex items-center justify-center bg-black/90 p-6 backdrop-blur-sm"
            onClick={() => setLightbox(null)}
            data-testid="gallery-lightbox"
          >
            <button className="absolute right-6 top-6 border border-white/20 p-3 text-white transition-colors hover:border-trx-blue" data-testid="lightbox-close" aria-label="Close">
              <X className="h-5 w-5" />
            </button>
            <motion.img
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              src={lightbox.src}
              alt={lightbox.label}
              className="max-h-[85vh] max-w-full border border-white/10 object-contain"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
